import {Chat, ComponentInterface} from "../types/chat";
import {WidgetConfig} from "../types/config";
import {ChatType} from "../types/enums";
import ComponentConfig from "../config/componentConfig";
import {waitForSocketConnection} from "./utils/socketUtils";
import AbstractChatConnector from "./chat/connector/abstractChatConnector";
import BotChatConnector from "./chat/connector/botChatConnector";
import AbstractWebsocketChat from "./abstractWebsocketChat";
import {ChatBotSummary} from "./chat/chatBotSummary";
import {MessageFactory} from "./factory/messageFactory";
import UserData from "./user/UserData";
import {ReportingEventFactory} from "./factory/reportingEventFactory";

export default class BotChat extends AbstractWebsocketChat implements Chat {

    private botSummary: null|ChatBotSummary = null;
    private summaryResolver: ((summary: ChatBotSummary) => void) | null = null;

    constructor(
        protected config: WidgetConfig,
        protected componentConfig: ComponentConfig,
        protected alpineComponent: ComponentInterface,
    ) {
        super(config, componentConfig, alpineComponent);
    }

    public setBotSummary(summary: ChatBotSummary): void
    {
        this.botSummary = summary;
        if (this.summaryResolver) {
            this.summaryResolver(summary);
            this.summaryResolver = null;
        }
    }

    public async getSummary():Promise<ChatBotSummary|null> {
        if (this.componentConfig.botChatId === null) {
            return null;
        }

        this.botSummary = null;
        let messageObject = MessageFactory.create(
            ChatType.BOT_CHAT,
            '',
            this.config.trademark,
            'Summary'
        );

        await this.sendMessage(messageObject, true);

        return this.waitForSummary(10000);
    }

    public async loadHistory():Promise<void> {
        this.alpineComponent.showBotGreeting();
        if (this.componentConfig.botChatId === null) {
            this.alpineComponent.showBotSuggestions();
            return;
        }

        await this.loadHistoryForCurrentChat();
    }

    public async onBotHistoryLoaded(): Promise<void> {
    }

    public showBotSuggestions(): void
    {
        this.alpineComponent.showBotSuggestions();
    }

    public async sendReportingEvent(messageId: string, responseId: string, url: string):Promise<void> {
        let webSocket = await this.getChatConnector().getWebSocket();
        if (webSocket === null) {
            return;
        }

        let subscriberId = 0
        let userSessionId = 'unknown';
        const userData = this.getChatConnector().getUserData();
        if(userData instanceof UserData) {
            subscriberId = userData.getSubscriberId();
            userSessionId = userData.getUserChatSessionId();
        }

        let reportingEvent = ReportingEventFactory.create(
            messageId,
            responseId,
            url,
            subscriberId,
            userSessionId,
            this.config.trademark,
            this.componentConfig.client
        );

        waitForSocketConnection(
            webSocket,
            this.getEventHandler().handleSendReportingEvent,
            () => { },
            reportingEvent
        )
    }

    public getChatConnector(): AbstractChatConnector
    {
        this.chatConnector ??= new BotChatConnector(
            this.config,
            this.componentConfig,
            this.getWssUrl(),
            this.getMessageHandler(),
            this.getEventHandler(),
            this.alpineComponent
        );

        return this.chatConnector;
    }

    public isBotChat(): boolean {
        return true;
    }

    protected getWssUrl(): string {
        return this.config.getWssUrlForBot();
    }

    protected getChatType(): string {
        return ChatType.BOT_CHAT;
    }

    protected async sendLeaveEvent(): Promise<void> {
    }

    protected getSessionId(): null|string {
        return this.componentConfig.getBotChatId();
    }

    private async waitForSummary(timeoutMs: number): Promise<ChatBotSummary | null> {
        const summaryPromise = new Promise<ChatBotSummary>(resolve => {
            this.summaryResolver = resolve;
        });

        const timeoutPromise = new Promise<null>(resolve =>
            setTimeout(() => {
                this.summaryResolver = null;
                resolve(null);
            }, timeoutMs)
        );

        return Promise.race([summaryPromise, timeoutPromise]);
    }
}